import React, { useState, useEffect } from "react";
import { terminalAPI } from "../../services/api";
import {
  usePOSCart,
  usePOSPayment,
  usePOSTerminal,
  STAFF_CANCELLED,
} from "../../context/POSContext";
import { logError } from "../../utils/errorUtils";
import "./POSPayment.css";

const POSPayment = ({ onComplete }) => {
  const { items, totals } = usePOSCart();
  const {
    step,
    paymentData,
    error: paymentError,
    errorCode: paymentErrorCode,
    cancelPayment,
    simulateTap,
    clearReader,
    retryPayment,
    cancelSale,
  } = usePOSPayment();
  const { selectedReader } = usePOSTerminal();
  const [canceling, setCanceling] = useState(false);
  const [endingSale, setEndingSale] = useState(false);

  // "Cancel sale": no charge, empty the cart, clear the reader screen.
  const handleCancelSale = async () => {
    if (!window.confirm("Cancel this sale? The cart will be emptied and the reader screen cleared.")) {
      return;
    }
    setEndingSale(true);
    await cancelSale();
    setEndingSale(false);
  };
  const [clearingReader, setClearingReader] = useState(false);
  const [readerClearMessage, setReaderClearMessage] = useState(null);
  useEffect(() => {
    if (step === "processing") setReaderClearMessage(null);
  }, [step]);

  // Issue #289: when the reader is busy with an old attempt (for example
  // after a page reload), staff can clear it. A paid payment is never
  // cancelled by the server.
  const handleClearReader = async () => {
    if (
      !window.confirm(
        "Another payment is waiting on the reader. Clear it? The customer will not be charged for it."
      )
    ) {
      return;
    }
    setClearingReader(true);
    const result = await clearReader();
    setClearingReader(false);
    setReaderClearMessage(
      result.success
        ? `${result.message || "The card reader was cleared."} Press Try again.`
        : result.error
    );
  };
  const isSimulatedReader = String(
    selectedReader?.device_type || selectedReader?.model || ""
  ).startsWith("simulated");

  const [customerEmail, setCustomerEmail] = useState("");
  // No tip is taken at the reader (payments capture automatically).
  const tipAmount = 0;
  const tipPercentage = 0;
  const [receiptSent, setReceiptSent] = useState(false);
  const [error, setError] = useState(null);

  // Check if totals are valid (cart might have been emptied)
  useEffect(() => {
    if (totals && totals.total === 0 && step === "success") {
      // If cart is empty during payment process, close the payment modal
      onComplete("failed");
      return;
    }
  }, [totals, step, onComplete]);

  // Format price from cents to dollars
  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Issue #289: the screen no longer captures payments (they capture
  // automatically on the server). The old capture code here, with its
  // 50-second "declined", was not used and is removed.

  // Helper function to send receipt
  const sendReceipt = async () => {
    try {
      await terminalAPI.createReceipt({
        payment_intent_id: paymentData.paymentIntentId,
        customer_email: customerEmail || paymentData.customerEmail,
      });
      setReceiptSent(true);
      return true;
    } catch (receiptError) {
      setError("Payment completed but receipt could not be sent.");
      return false;
    }
  };

  // Render different content based on payment step
  const renderContent = () => {
    switch (step) {
      case "processing":
        // Issue #289: one state while waiting. A slow tap is "waiting",
        // never "declined"; staff can cancel the payment on the reader.
        return (
          <div className="pos-payment-processing" data-testid="pos-payment-waiting">
            <div className="pos-payment-spinner"></div>
            <h2>Waiting for the card</h2>
            <p>
              Ask the customer to tap, insert, or swipe their card on the
              reader{totals ? ` (${formatPrice(totals.total)})` : ""}.
            </p>
            <div className="pos-payment-actions">
              {isSimulatedReader && (
                <button
                  type="button"
                  className="pos-payment-change-method-btn"
                  onClick={simulateTap}
                >
                  Simulate card tap (test mode)
                </button>
              )}
              <button
                type="button"
                className="pos-payment-change-method-btn"
                disabled={canceling}
                onClick={async () => {
                  setCanceling(true);
                  await cancelPayment();
                  setCanceling(false);
                }}
              >
                {canceling ? "Cancelling..." : "Cancel payment"}
              </button>
            </div>
          </div>
        );

      case "success":
        return (
          <div className="pos-payment-success">
            <div className="pos-payment-success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h2>Payment Successful!</h2>

            {paymentData && paymentData.requiresPaymentMethod && (
              <div className="pos-payment-warning">
                <h3>Payment Method Required for Subscription</h3>
                <p>
                  The customer has purchased a subscription, but they need to
                  add a payment method before the subscription can be created.
                  Please navigate to their account page to add a payment method.
                </p>
                <button
                  className="pos-navigate-payment-btn"
                  onClick={() => {
                    // Get the customer ID from paymentData
                    const customerId =
                      paymentData.customerId ||
                      (paymentData.transaction &&
                        paymentData.transaction.stripeCustomerId);

                    if (customerId) {
                      // Navigate to the customer's payment method page
                      window.open(
                        `/dashboard/customers/${customerId}/payment-methods`,
                        "_blank"
                      );
                    } else {
                      alert(
                        "Customer ID not found. Please navigate to the customer's profile manually."
                      );
                    }
                  }}
                >
                  <i className="fas fa-credit-card"></i> Add Payment Method
                </button>
                <p className="pos-payment-note">
                  After adding a payment method, the subscription will be
                  created automatically.
                </p>
              </div>
            )}

            {!totals || totals.total === 0 ? (
              <div className="pos-payment-error">
                Cart is empty. Please close this window and add items to cart.
              </div>
            ) : (
              <>
                <div className="pos-receipt" id="printable-receipt">
                  <div className="pos-receipt-header">
                    <h3>Diviti Adora Infrared & Red Light Studio</h3>
                    <p>1930 E Tangerine Rd Ste 100</p>
                    <p>Oro Valley, AZ 85755</p>
                    <p>(520) 555-1234</p>
                    <p>www.divitiadora.com</p>
                    <div className="pos-receipt-date">
                      <p>{new Date().toLocaleDateString()}</p>
                      <p>{new Date().toLocaleTimeString()}</p>
                    </div>
                  </div>

                  <div className="pos-receipt-items">
                    <table>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Qty</th>
                          <th>Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items && items.length > 0 ? (
                          items.map((item, index) => (
                            <tr key={index}>
                              <td>
                                {item.name}
                                {item.isProrated && (
                                  <div className="pos-receipt-prorated-note">
                                    (Prorated until{" "}
                                    {new Date(
                                      item.nextBillingDate
                                    ).toLocaleDateString()}
                                    )
                                  </div>
                                )}
                              </td>
                              <td>{item.quantity}</td>
                              <td>
                                {item.isProrated ? (
                                  <>
                                    <div className="pos-receipt-original-price">
                                      {formatPrice(item.originalPrice)}
                                    </div>
                                    <div className="pos-receipt-prorated-price">
                                      {formatPrice(item.unitPrice)}
                                    </div>
                                  </>
                                ) : (
                                  formatPrice(item.unitPrice)
                                )}
                              </td>
                              <td>{formatPrice(item.totalPrice)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4">No items available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="pos-receipt-summary">
                    <div className="pos-receipt-summary-row">
                      <span>Subtotal:</span>
                      <span>{formatPrice(totals.subtotal)}</span>
                    </div>
                    <div className="pos-receipt-summary-row">
                      <span>Tax (8.6%):</span>
                      <span>{formatPrice(totals.taxAmount)}</span>
                    </div>
                    {tipAmount > 0 && (
                      <div className="pos-receipt-summary-row">
                        <span>
                          Tip {tipPercentage > 0 ? `(${tipPercentage}%)` : ""}:
                        </span>
                        <span>{formatPrice(tipAmount)}</span>
                      </div>
                    )}
                    <div className="pos-receipt-summary-row pos-receipt-total">
                      <span>Total:</span>
                      <span>{formatPrice(totals.total + tipAmount)}</span>
                    </div>
                  </div>

                  <div className="pos-receipt-payment-method">
                    <p>Payment Method: Card</p>
                    {paymentData && (
                      <>
                        {paymentData.cardBrand && paymentData.last4 && (
                          <p>
                            {paymentData.cardBrand} •••• {paymentData.last4}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Display next billing date for subscriptions if available */}
                  {paymentData &&
                    paymentData.captureDetails &&
                    paymentData.captureDetails.transaction &&
                    paymentData.captureDetails.transaction.membershipItems && (
                      <div className="pos-receipt-subscription-info">
                        <h4>Subscription Information</h4>
                        {(() => {
                          try {
                            const membershipItems = JSON.parse(
                              paymentData.captureDetails.transaction
                                .membershipItems
                            );
                            return membershipItems.map((item, index) => {
                              // Find corresponding result in the processResults if available
                              const resultItem =
                                paymentData.captureDetails.processResults?.find(
                                  (r) =>
                                    r.type === "membership" &&
                                    r.results?.find(
                                      (sr) =>
                                        sr.membershipType ===
                                        item.membershipType
                                    )
                                );

                              const subscriptionResult =
                                resultItem?.results?.find(
                                  (sr) =>
                                    sr.membershipType === item.membershipType
                                );

                              const nextBillingDate =
                                subscriptionResult?.nextBillingDate
                                  ? new Date(
                                      subscriptionResult.nextBillingDate
                                    ).toLocaleDateString()
                                  : null;

                              return (
                                <div
                                  key={index}
                                  className="pos-receipt-subscription-item"
                                >
                                  <p>
                                    <strong>{item.membershipName}</strong>
                                  </p>
                                  {nextBillingDate && (
                                    <p>Next billing date: {nextBillingDate}</p>
                                  )}
                                </div>
                              );
                            });
                          } catch (e) {
                            return null;
                          }
                        })()}
                      </div>
                    )}

                  <div className="pos-receipt-footer">
                    <p>Thank you for your business!</p>
                    <p>
                      Transaction ID:{" "}
                      {paymentData ? paymentData.paymentIntentId : "N/A"}
                    </p>
                  </div>
                </div>

                <div className="pos-payment-email-section">
                  <h3>Email Receipt</h3>
                  <div className="pos-email-input-group">
                    <input
                      type="email"
                      placeholder="Customer email (optional)"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                    />
                    {paymentData.customerEmail && !customerEmail && (
                      <p className="pos-email-note">
                        Will use customer email: {paymentData.customerEmail}
                      </p>
                    )}
                  </div>
                </div>

                {error && <div className="pos-payment-error">{error}</div>}
                {receiptSent && (
                  <div className="pos-payment-receipt-sent">
                    Receipt sent successfully!
                  </div>
                )}

                <div className="pos-payment-actions">
                  <button
                    className="pos-print-receipt-btn"
                    onClick={() => {
                      const printContent =
                        document.getElementById("printable-receipt");
                      const originalContents = document.body.innerHTML;

                      // Create a new window for printing
                      const printWindow = window.open("", "_blank");

                      // Add print-specific styles
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Receipt</title>
                            <style>
                              body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                              .pos-receipt { width: 300px; margin: 0 auto; }
                              .pos-receipt-header { text-align: center; margin-bottom: 20px; }
                              .pos-receipt-header h3 { margin: 0 0 10px 0; }
                              .pos-receipt-header p { margin: 2px 0; }
                              .pos-receipt-date { margin-top: 10px; }
                              .pos-receipt-items { margin-bottom: 20px; }
                              table { width: 100%; border-collapse: collapse; }
                              th { border-bottom: 1px solid #ddd; text-align: left; padding: 5px; }
                              td { padding: 5px; }
                              .pos-receipt-summary { margin-top: 10px; }
                              .pos-receipt-summary-row { display: flex; justify-content: space-between; margin: 5px 0; }
                              .pos-receipt-total { font-weight: bold; border-top: 1px solid #000; padding-top: 5px; }
                              .pos-receipt-payment-method { margin: 15px 0; }
                              .pos-receipt-footer { text-align: center; margin-top: 20px; border-top: 1px solid #ddd; padding-top: 10px; }
                            </style>
                          </head>
                          <body>
                            ${printContent.outerHTML}
                          </body>
                        </html>
                      `);

                      printWindow.document.close();
                      printWindow.focus();

                      // Print after a short delay to ensure content is loaded
                      setTimeout(() => {
                        printWindow.print();
                        printWindow.close();
                      }, 250);
                    }}
                  >
                    <i className="fas fa-print"></i> Print Receipt
                  </button>

                  <button
                    className="pos-email-receipt-btn"
                    onClick={sendReceipt}
                    disabled={!customerEmail && !paymentData.customerEmail}
                  >
                    <i className="fas fa-envelope"></i> Email Receipt
                  </button>

                  <button
                    className="pos-payment-done-btn"
                    onClick={() => onComplete("success")}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        );

      case "failed": {
        // A staff "Cancel payment" is not a failure: nothing was charged.
        const staffCancelled = paymentErrorCode === STAFF_CANCELLED;
        return (
          <div className="pos-payment-failed" data-testid={staffCancelled ? "pos-payment-cancelled" : "pos-payment-failed"}>
            <div className="pos-payment-failed-icon">
              <i className={staffCancelled ? "fas fa-ban" : "fas fa-times-circle"}></i>
            </div>
            <h2>{staffCancelled ? "Payment cancelled" : "Payment Failed"}</h2>
            <p>
              {staffCancelled
                ? "The customer was not charged. The cart is kept."
                : "The payment did not go through. The cart is kept."}
            </p>
            {!staffCancelled && (
              <p className="pos-payment-error-details" data-testid="pos-payment-failure-reason">
                {/* Issue #289: show the real reason (Stripe's decline message). */}
                {error ||
                  paymentError ||
                  "Payment was declined or canceled. Please try again with the same or a different payment method."}
              </p>
            )}
            {paymentErrorCode === "POS_READER_BUSY" && (
              <div className="pos-reader-busy" data-testid="pos-reader-busy">
                <button
                  type="button"
                  className="pos-payment-change-method-btn"
                  onClick={handleClearReader}
                  disabled={clearingReader}
                >
                  {clearingReader ? "Clearing..." : "Clear reader"}
                </button>
              </div>
            )}
            {readerClearMessage && (
              <p className="pos-payment-note" data-testid="pos-reader-clear-message">
                {readerClearMessage}
              </p>
            )}
            <div className="pos-payment-actions">
              <button
                type="button"
                className="pos-payment-retry-btn"
                disabled={endingSale}
                onClick={() => retryPayment()}
              >
                Try again
              </button>
              {staffCancelled ? (
                <button
                  type="button"
                  className="pos-payment-change-method-btn"
                  disabled={endingSale}
                  onClick={() => onComplete("canceled")}
                >
                  Back to cart
                </button>
              ) : (
                <button
                  type="button"
                  className="pos-payment-change-method-btn"
                  disabled={endingSale}
                  onClick={async () => {
                    // POS-PAYMENTINTENT-CANCEL-001 (ADR-034): cancel the prior
                    // PaymentIntent before going back to the cart. A cleanup
                    // failure does not block staff.
                    if (paymentData && paymentData.paymentIntentId) {
                      try {
                        await terminalAPI.cancelPaymentIntent(
                          paymentData.paymentIntentId,
                          "card_present_failure_recovery"
                        );
                      } catch (err) {
                        const status = err?.response?.status;
                        if (status === 409) {
                          const errorCode = err?.response?.data?.code || "CANNOT_CANCEL";
                          setError(
                            errorCode === "CANNOT_CANCEL_SUCCEEDED"
                              ? "Cannot cancel — payment already completed. Use the refund flow if needed."
                              : "Cannot cancel — payment is awaiting capture. Use the refund flow if needed."
                          );
                          logError(err, "POSPayment:cancelPaymentIntent:409");
                        } else {
                          logError(err, "POSPayment:cancelPaymentIntent");
                          setError("Cleanup failed — flag a manager if this recurs.");
                        }
                      }
                    }
                    onComplete("failed");
                  }}
                >
                  Change payment method
                </button>
              )}
              <button
                type="button"
                className="pos-payment-cancel-sale-btn"
                disabled={endingSale}
                onClick={handleCancelSale}
              >
                {endingSale ? "Cancelling sale..." : "Cancel sale"}
              </button>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="pos-payment-overlay">
      <div className="pos-payment-modal">{renderContent()}</div>
    </div>
  );
};

export default POSPayment;
